// React-Query hooks для Журнала визитов (Mini-sprint 6-visits Stage 4 §3).
// Source of truth: docs/specs/sprint-6-visits/stage-4-frontend.md §3.
// R1: axios `api` (Bearer interceptor) + централизованный queryKeys umbrella.

import {
  keepPreviousData,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { api } from '@/api/client';
import { queryKeys } from '@/api/queryKeys';
import type {
  Visit,
  ListVisitsResponse,
  CreateVisitRequest,
  UpdateVisitRequest,
  VisitAttachment,
} from '@/api/types';

// ── List (paginated, «Показать ещё») ──────────────────────────────
// Backend отдаёт per_page=20; getNextPageParam ведёт к concat'у страниц.
// Поиск (q) меняет queryKey → свежий infinite-query с 1-й страницы.
export function useVisitsInfinite(q?: string) {
  return useInfiniteQuery({
    queryKey: queryKeys.visitsList({ q }),
    queryFn: async ({ pageParam }) => {
      const r = await api.get<ListVisitsResponse>('/visits', {
        params: { q, page: pageParam },
      });
      return r.data;
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage) =>
      lastPage.meta.current_page < lastPage.meta.last_page
        ? lastPage.meta.current_page + 1
        : undefined,
    placeholderData: keepPreviousData,
  });
}

// ── Single ────────────────────────────────────────────────────────
export function useVisit(id: number | string | undefined) {
  return useQuery({
    queryKey: queryKeys.visit(Number(id)),
    queryFn: async () => {
      const r = await api.get<{ data: Visit }>(`/visits/${id}`);
      return r.data.data;
    },
    enabled: id !== undefined && id !== null && id !== '',
  });
}

// ── Create ────────────────────────────────────────────────────────
export function useCreateVisitMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: CreateVisitRequest) => {
      const r = await api.post<{ data: Visit }>('/visits', payload);
      return r.data.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.visits });
    },
  });
}

// ── Update ────────────────────────────────────────────────────────
export function useUpdateVisitMutation(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: UpdateVisitRequest) => {
      const r = await api.patch<{ data: Visit }>(`/visits/${id}`, payload);
      return r.data.data;
    },
    onSuccess: (data) => {
      qc.setQueryData(queryKeys.visit(id), data);
      void qc.invalidateQueries({ queryKey: queryKeys.visits });
    },
  });
}

// ── Delete ────────────────────────────────────────────────────────
export function useDeleteVisitMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      await api.delete(`/visits/${id}`);
    },
    onSuccess: (_, id) => {
      qc.removeQueries({ queryKey: queryKeys.visit(id) });
      void qc.invalidateQueries({ queryKey: queryKeys.visits });
    },
  });
}

// ── Attachment upload (edit-flow hook) ────────────────────────────
export function useUploadVisitAttachmentMutation(visitId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (file: File) => {
      const form = new FormData();
      form.append('file', file);
      // axios сам выставит multipart boundary для FormData.
      const r = await api.post<{ data: VisitAttachment }>(`/visits/${visitId}/attachments`, form);
      return r.data.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.visit(visitId) });
    },
  });
}

/**
 * Прямой axios-call для attachment upload — используется в VisitForm.onSubmit
 * после create-visit, когда visitId известен только в момент вызова (нельзя
 * вызывать hook в callback'е). Caller сам редиректит — invalidation не нужен.
 */
export async function uploadVisitAttachmentDirect(visitId: number, file: File): Promise<VisitAttachment> {
  const form = new FormData();
  form.append('file', file);
  const r = await api.post<{ data: VisitAttachment }>(`/visits/${visitId}/attachments`, form);
  return r.data.data;
}

// ── Attachment delete ─────────────────────────────────────────────
export function useDeleteVisitAttachmentMutation(visitId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (attachmentId: number) => {
      await api.delete(`/visits/${visitId}/attachments/${attachmentId}`);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.visit(visitId) });
    },
  });
}

// ── Attachment signed-URL fetch (R1: JSON, не redirect) ───────────
/**
 * Backend возвращает {signed_url, expires_at, mime_type}. VisitDetail делает
 * mutation → window.open(signed_url). Auth: axios interceptor вставляет Bearer.
 */
export function useFetchVisitAttachmentUrl(visitId: number) {
  return useMutation({
    mutationFn: async (attachmentId: number) => {
      const r = await api.get<{ signed_url: string; expires_at: string; mime_type: string }>(
        `/visits/${visitId}/attachments/${attachmentId}/download`,
      );
      return r.data;
    },
  });
}
